import { useState } from "react";
import { Link } from "react-router-dom";
import { SERVICE_CATEGORIES, URGENCIES, useRfqs, type RfqStatus } from "../api/rfqs.js";

/**
 * Customer RFQ list (ops customers section). Defaults to the OPEN work queue
 * (submitted + in-review) — the daily triage view — with status tabs. Rows
 * link to the RFQ detail/support screen.
 *
 * The queue's job is to answer "what do I pick up next", so it leads with
 * urgency: an emergency water leak used to sit here looking exactly like a
 * flexible repaint, because the only ordering was newest-first. Photo count
 * and a note marker are here for the same reason — they save opening a row to
 * find out there was nothing to see.
 */
const TABS: { label: string; status: string }[] = [
  { label: "Open queue", status: "OPEN" },
  { label: "Submitted", status: "SUBMITTED" },
  { label: "In review", status: "IN_REVIEW" },
  { label: "Closed", status: "CLOSED" },
  { label: "Cancelled", status: "CANCELLED" },
];

const statusStyles: Record<RfqStatus, string> = {
  SUBMITTED: "bg-amber-100 text-amber-800",
  IN_REVIEW: "bg-brand-100 text-brand-800",
  CLOSED: "bg-field-gray text-charcoal",
  CANCELLED: "bg-rose-100 text-rose-700",
};

/** Loud for the ones that cannot wait, quiet for the ones that can. */
const urgencyStyles: Record<string, string> = {
  emergency: "bg-rose-100 text-rose-700",
  urgent: "bg-amber-100 text-amber-800",
  normal: "bg-field-gray text-charcoal",
  flexible: "bg-field-gray text-charcoal",
};

/** Short forms — the queue has no room for "Emergency · within 24h". */
const urgencyShort: Record<string, string> = {
  emergency: "Emergency",
  urgent: "Urgent",
  normal: "Normal",
  flexible: "Flexible",
};

function categoryLabel(value: string): string {
  return SERVICE_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function urgencyLabel(value: string): string {
  return urgencyShort[value] ?? URGENCIES.find((u) => u.value === value)?.label ?? value;
}

export function Customers() {
  const [tab, setTab] = useState(0);
  const { data, isPending, isError, refetch } = useRfqs(TABS[tab]!.status);
  const items = data?.items ?? [];

  return (
    <div>
      <h1 className="text-xl font-bold text-charcoal">Customer requests</h1>

      <div className="mt-4 flex flex-wrap gap-1 border-b border-charcoal/15">
        {TABS.map((t, i) => (
          <button
            key={t.label}
            type="button"
            onClick={() => setTab(i)}
            className={[
              "px-4 py-2 text-sm font-medium",
              i === tab
                ? "border-b-2 border-brand-700 text-brand-800"
                : "text-charcoal hover:text-charcoal",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isPending ? (
        <p className="mt-6 text-sm text-charcoal">Loading…</p>
      ) : isError ? (
        <div className="mt-6">
          <p className="text-sm text-charcoal">Couldn&rsquo;t load requests.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 rounded-lg border border-charcoal/30 px-3 py-1.5 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-charcoal">Nothing here.</p>
      ) : (
        <table className="mt-4 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-charcoal/15 text-left text-xs uppercase tracking-wide text-charcoal">
              <th className="py-2 pr-4 font-medium">Request</th>
              <th className="py-2 pr-4 font-medium">Category</th>
              <th className="py-2 pr-4 font-medium">Urgency</th>
              <th className="py-2 pr-4 font-medium">Location</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const photos = r.images?.length ?? 0;
              const notes = r.adminNotes?.length ?? 0;
              return (
                <tr key={r.id} className="border-b border-charcoal/10 hover:bg-white">
                  <td className="py-2 pr-4">
                    <Link
                      to={`/customers/${r.id}`}
                      className="font-medium text-brand-700 hover:underline line-clamp-1"
                    >
                      {r.description.slice(0, 60) || "(no description)"}
                    </Link>
                    <div className="flex gap-2 text-xs text-charcoal">
                      {photos > 0 && (
                        <span title={`${photos} photo${photos === 1 ? "" : "s"}`}>
                          {photos} photo{photos === 1 ? "" : "s"}
                        </span>
                      )}
                      {notes > 0 && (
                        <span title={`${notes} triage note${notes === 1 ? "" : "s"}`}>
                          {notes} note{notes === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-charcoal">{categoryLabel(r.serviceCategory)}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        urgencyStyles[r.urgency] ?? "bg-field-gray text-charcoal"
                      }`}
                    >
                      {urgencyLabel(r.urgency)}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-charcoal">
                    {r.location.city}, {r.location.state} {r.location.postalCode}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[r.status]}`}
                    >
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-charcoal">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
